/**
 * /stats — S-32 "Your numbers" (ledger #92, V9 stats-social).
 *
 * Private by default: everything on this screen is the OWN account's
 * aggregates (server-computed, #78/§7.2 — received verdicts are bare counts,
 * never identities). Gamer-styled stat sheet + badge case, the strictly
 * opt-in leaderboard toggle (consequence note ©stats.leaderboard_note) and
 * the link to /stats/leaderboard.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { animate, motion } from "framer-motion";
import { copy, resolveCopy } from "@humble/shared";
import {
  Badge,
  Card,
  Mascot,
  rise,
  Skeleton,
  stagger,
  toast,
  usePrefersReducedMotion,
} from "../../design/index.js";
import { api } from "../../lib/api.js";
import { ws } from "../../lib/ws.js";
import "./stats.css";

export interface MyStats {
  rejects: { given: number; received: number };
  reject_matches: number;
  matches_total: number;
  reject_match_conversion: number;
  super_rejects: { fired: number; survived: number };
  streak_best: number;
  shield_saves: number;
  honor_points: number;
  profile_views_30d: number;
  leaderboard_optin: boolean;
}

/** /me/stats is additionalProperties:true in the spec — narrow it here. */
async function fetchMyStats(): Promise<MyStats> {
  return (await api.get("/me/stats")) as unknown as MyStats;
}

/** Badge case — cosmetic milestones derived from server numbers (render-only:
 *  every threshold input is a server aggregate; the client invents nothing). */
function badgesOf(s: MyStats): Array<{ id: string; label: string; earned: boolean; hint: string }> {
  return [
    {
      id: "first-blood",
      label: "FIRST REJECTION",
      earned: s.rejects.received >= 1,
      hint: "Get rejected once. Everyone starts somewhere.",
    },
    {
      id: "century",
      label: "REJECTED ×100",
      earned: s.rejects.received >= 100,
      hint: "One hundred people said no. Statistically, a personality.",
    },
    {
      id: "hater",
      label: "PROFESSIONAL HATER",
      earned: s.rejects.given >= 50,
      hint: "Fifty rejects given. The left swipe is a verb.",
    },
    {
      id: "alchemist",
      label: "HATE ALCHEMIST",
      earned: s.reject_matches >= 1,
      hint: "Turn one mutual rejection into a match.",
    },
    {
      id: "survivor",
      label: "SR SURVIVOR",
      earned: s.super_rejects.survived >= 1,
      hint: "Take a Super Reject and come back. Respect.",
    },
    {
      id: "honorable",
      label: "HONORABLE",
      earned: s.honor_points >= 1,
      hint: "One mutual GG. Sportsmanship confirmed.",
    },
    {
      id: "streaker",
      label: "ON FIRE",
      earned: s.streak_best >= 3,
      hint: "A 3-match killstreak, at some point in history.",
    },
    {
      id: "guarded",
      label: "THE WALL",
      earned: s.shield_saves >= 1,
      hint: "Block a Super Reject with an armed Shield.",
    },
  ];
}

/** High-score count-up: ticks from 0 to the server number once on mount.
 *  Tabular numerals prevent jitter; reduced motion renders the final value. */
function CountUp({ value }: { value: number }) {
  const reduced = usePrefersReducedMotion();
  const [shown, setShown] = useState(() => (reduced ? value : 0));
  useEffect(() => {
    if (reduced) {
      setShown(value);
      return;
    }
    const controls = animate(0, value, {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setShown(Math.round(v)),
    });
    return () => controls.stop();
  }, [value, reduced]);
  return <>{shown}</>;
}

/** One tile of the score sheet. */
function StatTile({
  value,
  label,
  motif,
  suffix,
}: {
  value: number;
  label: string;
  motif?: "reject" | "accent" | "gold" | "shield" | "honor";
  suffix?: string;
}) {
  return (
    <motion.div variants={rise}>
      <Card padding="md" className={`stat-tile${motif ? ` stat-tile--${motif}` : ""}`}>
        <div className="stat-tile__big">
          <CountUp value={value} />
          {suffix}
        </div>
        <div className="stat-tile__label">{label}</div>
      </Card>
    </motion.div>
  );
}

export function StatsScreen() {
  const [stats, setStats] = useState<MyStats | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetchMyStats()
      .then(setStats)
      .catch(() => setStats(null));
  }, []);

  useEffect(() => {
    load();
    const off = ws.on("gg.completed", load); // honor total moved — re-render
    return off;
  }, [load]);

  const toggleOptin = async (optin: boolean) => {
    if (!stats || saving) return;
    setSaving(true);
    const prev = stats.leaderboard_optin;
    setStats({ ...stats, leaderboard_optin: optin });
    try {
      await api.put("/me/leaderboard-optin", { body: { optin } });
      toast(optin ? "You're on the board (once ranked)." : "Off the board. Immediately.", "info");
    } catch {
      setStats((s) => (s ? { ...s, leaderboard_optin: prev } : s));
      toast("Couldn't update the leaderboard setting. Try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (stats === null) {
    return (
      <div className="stats-screen">
        <header className="stats-head">
          <p className="h-caps stats-kicker">The stat sheet</p>
          <h1 className="type-display-3">Your numbers</h1>
        </header>
        <div className="stats-grid" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height={104} radius={20} />
          ))}
        </div>
      </div>
    );
  }

  const empty =
    stats.rejects.given === 0 &&
    stats.rejects.received === 0 &&
    stats.matches_total === 0 &&
    stats.super_rejects.fired === 0;

  return (
    <div className="stats-screen">
      <header className="stats-head">
        <p className="h-caps stats-kicker">The stat sheet</p>
        <h1 className="type-display-3">Your numbers</h1>
        <p className="type-editorial stats-lede">{resolveCopy("stats.lede")}</p>
      </header>

      {empty ? (
        <Card padding="lg" style={{ display: "flex", justifyContent: "center" }}>
          <Mascot mood="ko" quip={copy.stats.empty} />
        </Card>
      ) : (
        <>
          <motion.div className="stats-grid" variants={stagger} initial="initial" animate="enter">
            <StatTile value={stats.rejects.received} label="rejections received" motif="reject" />
            <StatTile value={stats.rejects.given} label="rejections dealt" motif="reject" />
            <StatTile
              value={Math.round(stats.reject_match_conversion * 100)}
              suffix="%"
              label={`reject → match conversion (${stats.reject_matches} of ${stats.rejects.given})`}
              motif="accent"
            />
            <StatTile value={stats.matches_total} label="matches, all kinds" />
            <StatTile value={stats.streak_best} label="best killstreak" motif="gold" />
            <StatTile value={stats.shield_saves} label="shield saves" motif="shield" />
            <StatTile value={stats.super_rejects.survived} label="Super Rejects survived" />
            <StatTile value={stats.honor_points} label="GG honor points" motif="honor" />
            <StatTile value={stats.profile_views_30d} label="profile views (30 d, count only)" />
          </motion.div>

          <h2 className="stats-h2">Badge case</h2>
          <div className="stats-badges">
            {badgesOf(stats).map((b) => (
              <Card
                key={b.id}
                padding="md"
                className={`stats-badge${b.earned ? " stats-badge--earned" : ""}`}
              >
                <Badge className="stats-badge__chip">{b.label}</Badge>
                <div className="text-faint stats-badge__hint">{b.hint}</div>
              </Card>
            ))}
          </div>
        </>
      )}

      <h2 className="stats-h2">{copy.stats.leaderboard_title}</h2>
      <Card padding="md" className="stats-optin">
        <label className="stats-optin__row">
          <input
            type="checkbox"
            checked={stats.leaderboard_optin}
            disabled={saving}
            onChange={(e) => void toggleOptin(e.target.checked)}
          />
          <span>Put me on the leaderboard</span>
        </label>
        {/* Consequence note — serious, plain text (S-32 normative). */}
        <p className="stats-optin__note">{copy.stats.leaderboard_note}</p>
        <Link to="/stats/leaderboard" className="stats-optin__link">
          View the board →
        </Link>
      </Card>
    </div>
  );
}
